import { Injectable } from '@angular/core';
import { DashboardTileType, initialNumberOfInstallationsInColumn } from '@store';
import { forkJoin, Observable, of, OperatorFunction } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ClientService } from '../../client.service';
import { GenericDataResponse } from '../analytics/analytics.model';
import {
  DashboardAddColumnPayload,
  DashboardApiModel,
  DashboardColumn,
  DashboardEditColumnPayload,
  DashboardFilterPayload,
  DashboardFilterResponse,
  DashboardInstallationData,
  DashboardInstallationsFilterValuesType,
  DashboardInstallationsPayload,
  DashboardInstallationsStatusEnum,
  GetColumnInstallationsPayload,
  DashboardColumnDataWithoutInstallationDetails,
  PaginationPayload,
  ButtonFilterData,
  ButtonFilterDataName,
  ButtonFilterDataPayload,
  errorButtonFilterDataPayload,
  offlineButtonFilterDataPayload,
  worksProperlyButtonFilterDataPayload,
  maintenanceButtonFilterDataPayload,
  anomaliesButtonFilterDataPayload,
  DashboardInstallationsSumsPayload,
  DashboardInstallationsSumsResponse,
  SortOrder,
  DashboardInstallationListResponse,
  GenericSumsFilterDataPayload,
  MAXIMUM_DASHBOARD_LIMIT,
} from './dashboard.api-model';

@Injectable({ providedIn: 'root' })
export class DashboardApi implements DashboardApiModel {
  private readonly baseUrl = 'iot/v1/dashboard';
  private readonly baseUrlV2 = 'iot/v2/dashboard';

  constructor(private clientService: ClientService) {}

  getDashboard(
    maxInstPerColumn = initialNumberOfInstallationsInColumn
  ): Observable<GenericDataResponse<DashboardColumn[]>> {
    const endpoint = this.baseUrl + '/dashboard';

    return this.clientService.get(endpoint, {
      maxInstPerColumn,
    });
  }

  recreateSnapshot(
    maxInstPerColumn = initialNumberOfInstallationsInColumn
  ): Observable<GenericDataResponse<DashboardColumn[]>> {
    const endpoint = this.baseUrl + '/dashboard';

    return this.clientService.get(endpoint, {
      maxInstPerColumn,
      refresh: true,
    });
  }

  getFilters(): Observable<GenericDataResponse<DashboardFilterResponse[]>> {
    const endpoint = this.baseUrl + '/filters';

    return this.clientService.get(endpoint);
  }

  setFilters(filters: DashboardFilterPayload): Observable<GenericDataResponse<DashboardColumn[]>> {
    const endpoint = this.baseUrl + '/dashboard';

    return this.clientService.post(endpoint, filters, {
      maxInstPerColumn: initialNumberOfInstallationsInColumn,
    });
  }

  addColumn(body: DashboardAddColumnPayload): Observable<GenericDataResponse<DashboardColumn>> {
    const endpoint = this.baseUrl + '/columns';

    return this.clientService.post(endpoint, body);
  }

  deleteColumn(id: number, version: string): Observable<GenericDataResponse<null>> {
    const endpoint = this.baseUrl + '/columns/' + id;

    return this.clientService.delete(endpoint, {}, { version });
  }

  editColumn(
    id: number,
    body: DashboardEditColumnPayload
  ): Observable<GenericDataResponse<DashboardColumnDataWithoutInstallationDetails>> {
    const endpoint = this.baseUrl + '/columns/' + id;

    return this.clientService.put(endpoint, body);
  }

  fetchInstallations(
    data: DashboardInstallationsPayload,
    withQuickView = false
  ): Observable<GenericDataResponse<DashboardInstallationData[]>> {
    const endpoint = this.baseUrl + `/installations`;
    const params: PaginationPayload = {
      offset: data.offset,
      limit: data.limit,
      withQuickView: withQuickView,
    };
    const body = {
      [data.filter.filterName]: data.filter.values,
    };

    if (data.filter.modifier === DashboardTileType.InstallationsWithFault) {
      return this.fetchInstallationsForInstallationsWithFaultTile(
        data.filter.values,
        endpoint,
        params,
        body
      );
    }

    return this.clientService.post(endpoint, body, params);
  }

  private fetchInstallationsForInstallationsWithFaultTile(
    value: DashboardInstallationsFilterValuesType,
    endpoint: string,
    params: PaginationPayload,
    body: {}
  ): Observable<GenericDataResponse<DashboardInstallationData[]>> {
    switch (value[0]) {
      default:
        return this.clientService.post(endpoint, body, params);

      case DashboardInstallationsStatusEnum.Error:
        return this.clientService.post(endpoint, body, params).pipe(
          map((response: GenericDataResponse<DashboardInstallationData[]>) => {
            const filteredInstallations: DashboardInstallationData[] = response.data.filter(
              (installation: DashboardInstallationData): boolean => !installation.isSystemLocked
            );
            return { ...response, data: filteredInstallations } as GenericDataResponse<
              DashboardInstallationData[]
            >;
          })
        );

      case DashboardInstallationsStatusEnum.Locked:
        return this.clientService.post(endpoint, body, params).pipe(
          map((response: GenericDataResponse<DashboardInstallationData[]>) => {
            return { ...response, data: [] } as GenericDataResponse<DashboardInstallationData[]>;
          })
        );
    }
  }

  countInstallationsForFilters(
    bodyData: DashboardInstallationsSumsPayload[]
  ): Observable<GenericDataResponse<DashboardInstallationsSumsResponse[]>> {
    const endpoint = this.baseUrl + `/installations/sums`;

    return this.clientService.post(endpoint, bodyData);
  }

  fetchInstallationForColumn(
    { columnId, limit, offset, withQuickView }: GetColumnInstallationsPayload,
    filters: DashboardFilterPayload,
    _sortOrder: SortOrder
  ): Observable<GenericDataResponse<DashboardInstallationData[]>> {
    const endpoint = this.baseUrl + '/installations';

    return this.clientService.post(endpoint, filters, {
      columnId,
      // Request is done when various scenarios, e.g. when re-fetching data of column after initial error response.
      // In such case column could have no installations and limiting it to 0 causes an error.
      // Limit is optional property.
      ...(limit > 0 && { limit }),
      offset,
      withQuickView,
    });
  }

  moveKanbanInstallation(
    body: [
      DashboardColumnDataWithoutInstallationDetails,
      DashboardColumnDataWithoutInstallationDetails
    ]
  ): Observable<
    GenericDataResponse<
      [DashboardColumnDataWithoutInstallationDetails, DashboardColumnDataWithoutInstallationDetails]
    >
  > {
    const endpoint = this.baseUrl + '/columns';

    return this.clientService.put(endpoint, body);
  }

  /**
   * V1 and V2 differs in pagination approach and response type
   * V1 uses offset and limit, returns installations solely
   * V2 uses limit and cursor, returns installations and further cursor data
   */
  getFilteredInstallationsV1(
    offset: number,
    limit: number,
    body: DashboardFilterPayload,
    withQuickView = true
  ): Observable<GenericDataResponse<DashboardInstallationData[]>> {
    const endpoint = this.baseUrl + '/installations';

    return this.clientService.post(endpoint, body, { offset, limit, withQuickView });
  }

  getFilteredInstallationsV2(
    limit: number,
    body: DashboardFilterPayload,
    cursor?: string
  ): Observable<DashboardInstallationListResponse> {
    const endpoint = this.baseUrlV2 + '/installations';

    return this.clientService.post(endpoint, body, { limit, ...(cursor && { cursor }) });
  }

  // Very unlikely but possible - handle case when installation reach more than 1k invitations
  fetchInvitationsRecursively(
    filter: DashboardFilterPayload,
    limit = MAXIMUM_DASHBOARD_LIMIT
  ): OperatorFunction<DashboardInstallationListResponse, DashboardInstallationData[]> {
    return (
      inputObservable: Observable<DashboardInstallationListResponse>
    ): Observable<DashboardInstallationData[]> =>
      inputObservable.pipe(
        switchMap((res) => {
          if (res?.cursor?.next) {
            return this.getFilteredInstallationsV2(limit, filter, res.cursor.next).pipe(
              switchMap((nextRes) =>
                forkJoin([
                  of(res.data),
                  of(nextRes).pipe(this.fetchInvitationsRecursively(filter, limit)),
                ]).pipe(
                  map(([previousRequest, latestRequest]) => [...previousRequest, ...latestRequest])
                )
              )
            );
          }

          // latestRequest
          return of(res.data);
        })
      );
  }

  getInstallationListFilterButtonData(): Observable<GenericDataResponse<ButtonFilterData[]>> {
    const endpoint = this.baseUrl + '/installations/sums';

    const body: ButtonFilterDataPayload[] = [
      { name: ButtonFilterDataName.All, filters: {} },
      {
        name: ButtonFilterDataName.Error,
        filters: errorButtonFilterDataPayload,
      },
      {
        name: ButtonFilterDataName.Offline,
        filters: offlineButtonFilterDataPayload,
      },
      { name: ButtonFilterDataName.Working, filters: worksProperlyButtonFilterDataPayload },
      { name: ButtonFilterDataName.Maintenance, filters: maintenanceButtonFilterDataPayload },
      { name: ButtonFilterDataName.Anomalies, filters: anomaliesButtonFilterDataPayload },
    ];

    return this.clientService.post(endpoint, body, { sumRequests: 10 });
  }

  getSumsData(
    body: GenericSumsFilterDataPayload[]
  ): Observable<GenericDataResponse<ButtonFilterData[]>> {
    const endpoint = this.baseUrl + '/installations/sums';

    return this.clientService.post(endpoint, body);
  }
}
